import React, { useState, useEffect } from 'react';
import { getApiUrl, apiFetch } from '../../config/api';
import { useLanguage } from '../../contexts/LanguageContext';
import './UOMSelect.css';

/**
 * LocationSelect - Reusable warehouse location component with server-side search
 *
 * Usage:
 *   <LocationSelect
 *     value={data.destination_location}
 *     onChange={(value) => handleChange('destination_location', value)}
 *     disabled={!editMode}
 *   />
 *
 * Props:
 *   - value: Current location code (e.g., 'WAREHOUSE')
 *   - onChange: Callback function receiving the new location code
 *   - disabled: Whether the input is disabled
 *   - filterTypes: Array of location_type to show (e.g., ['BULK', 'PALLET', 'SHELF', 'STAGING'])
 *   - className: Additional CSS classes
 *   - placeholder: Placeholder text when no value selected
 *   - required: Whether field is required
 *   - name: Form field name
 *   - id: Element ID
 */
function LocationSelect({
  value,
  onChange,
  disabled = false,
  filterTypes = null,
  className = '',
  placeholder = null,
  required = false,
  name = 'location',
  id = null
}) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(value || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { currentLanguage } = useLanguage();

  // Sync searchText when value prop changes externally
  useEffect(() => {
    if (value && locations.length > 0) {
      const loc = locations.find(l => l.location_code === value);
      if (loc) {
        setSearchText(`${loc.location_code} - ${loc.location_name}`);
      } else {
        setSearchText(value);
      }
    } else if (!value) {
      setSearchText('');
    }
  }, [value, locations]);

  // Debounce search — wait 300ms after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch locations when debounced search changes
  useEffect(() => {
    fetchLocations(debouncedSearch);
  }, [debouncedSearch]);

  const fetchLocations = async (search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ is_active: 'true', page_size: '50' });
      if (search) params.set('search', search);
      const response = await apiFetch(getApiUrl(`/api/warehouse-locations?${params}`));
      if (response.ok) {
        const data = await response.json();
        setLocations(data || []);
      } else {
        console.error('Failed to fetch warehouse locations');
        setLocations([]);
      }
    } catch (error) {
      console.error('Error fetching warehouse locations:', error);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredLocations = filterTypes
    ? locations.filter(loc => filterTypes.includes(loc.location_type))
    : locations;

  const defaultPlaceholder = currentLanguage === 'es'
    ? 'Buscar ubicación...'
    : 'Search location...';

  const datalistId = id || `location-list-${name}`;

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        list={datalistId}
        id={id || `location-select-${name}`}
        name={name}
        value={searchText}
        onChange={(e) => {
          const val = e.target.value;
          setSearchText(val);
          setSearchTerm(val);
          const match = filteredLocations.find(loc =>
            `${loc.location_code} - ${loc.location_name}` === val ||
            loc.location_code === val
          );
          if (match) {
            if (onChange) onChange(match.location_code);
          } else if (!val) {
            if (onChange) onChange('');
          }
        }}
        disabled={disabled || loading}
        required={required}
        className={`uom-select ${className} ${loading ? 'loading' : ''}`}
        placeholder={placeholder || defaultPlaceholder}
      />
      <datalist id={datalistId}>
        {filteredLocations.map(loc => (
          <option key={loc.location_code} value={`${loc.location_code} - ${loc.location_name}`} />
        ))}
      </datalist>
    </div>
  );
}

export default LocationSelect;
